import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Certificate } from 'app/library/certificate/models/certificate.model';
import { CertificateState } from 'app/library/certificate/models/certificate.state';
import { SetModuleCertificates } from 'app/library/module-certificate/actions';
import { AddModuleCertificates } from 'app/library/module-certificate/actions/add-entities.action';
import { ModuleCertificateExtended } from 'app/library/module-certificate/models/module-certificate.extended.model';
import { ModuleCertificate } from 'app/library/module-certificate/models/module-certificate.model';
import { ModuleCertificateState } from 'app/library/module-certificate/models/module-certificate.state';
import { ModuleState } from 'app/library/module/models/module.state';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { UtilsState } from 'app/projects/subentity/src/lib/models/utils.state';
import { map } from 'rxjs/operators';
import { AddCertificatesToModuleSuccess, FetchModuleCertificates, FetchModuleCertificatesFailure, FetchModuleCertificatesSuccess, UnsetModuleCertificates } from '../actions';
import { ModuleCertificatesApiService } from '../services/module-certificates.api-service';
import { ModuleCertificatesStateModel } from './module-certificates.state-model';

@State<ModuleCertificatesStateModel>({
    name: 'moduleCertificates',
    defaults: new ModuleCertificatesStateModel(),
})
@Injectable()
export class ModuleCertificatesState extends SubentityState<ModuleCertificate> {
    @Selector([ModuleCertificateState.getEntitiesAsMap])
    static getEntities(state: ModuleCertificatesStateModel, moduleCertificates: Map<string, ModuleCertificate>): ModuleCertificate[] {
        if (!moduleCertificates) {
            return null;
        }

        return UtilsState.getEntities(state, moduleCertificates);
    }

    @Selector([ModuleCertificateState.getEntitiesAsMap, CertificateState.getEntities])
    static getEntitiesWithCertificate(
        state: ModuleCertificatesStateModel,
        moduleCertificates: Map<string, ModuleCertificate>,
        certificates: Certificate[]
    ): ModuleCertificateExtended[] {
        const toReturn = ModuleCertificatesState.getEntities(state, moduleCertificates);

        if (toReturn && certificates) {
            const certificatesContainer = {};

            certificates.forEach((certificate) => {
                certificatesContainer[certificate.id] = certificate;
            });

            return toReturn.map<ModuleCertificateExtended>((moduleCertificate) =>
                Object.assign<ModuleCertificateExtended, ModuleCertificate, Partial<ModuleCertificateExtended>>(new ModuleCertificateExtended(), moduleCertificate, {
                    certificate: certificatesContainer[moduleCertificate.certificateId] || null,
                })
            );
        }

        return null;
    }

    @Selector([ModuleCertificateState.getEntitiesAsMap])
    static getFilteredEntities(state: ModuleCertificatesStateModel, moduleCertificates: Map<string, ModuleCertificate>): ModuleCertificate[] {
        if (!moduleCertificates) {
            return null;
        }

        return UtilsState.getFilteredEntities(state, moduleCertificates);
    }

    @Selector([ModuleCertificateState.getEntitiesAsMap, CertificateState.getEntities])
    static getFilteredEntitiesWithCertificate(
        state: ModuleCertificatesStateModel,
        moduleCertificates: Map<string, ModuleCertificate>,
        certificates: Certificate[]
    ): ModuleCertificateExtended[] {
        const toReturn = ModuleCertificatesState.getFilteredEntities(state, moduleCertificates);

        if (toReturn && certificates) {
            const certificatesContainer = {};

            certificates.forEach((certificate) => {
                certificatesContainer[certificate.id] = certificate;
            });

            return toReturn.map<ModuleCertificateExtended>((moduleCertificate) =>
                Object.assign<ModuleCertificateExtended, ModuleCertificate, Partial<ModuleCertificateExtended>>(new ModuleCertificateExtended(), moduleCertificate, {
                    certificate: certificatesContainer[moduleCertificate.certificateId] || null,
                })
            );
        }

        return null;
    }

    @Selector()
    static isFetchingList(state: ModuleCertificatesStateModel): boolean {
        return state.fetchingList;
    }

    @Selector()
    static getSelectedEntityIds(state: ModuleCertificatesStateModel): string[] {
        return state.selected;
    }

    constructor(private _moduleCertificatesService: ModuleCertificatesApiService, private store: Store) {
        super(null, 0);
    }

    @Action(SetModuleCertificates)
    setAll(ctx: StateContext<ModuleCertificatesStateModel>, action: SetModuleCertificates): void {
        this._setSubentities(ctx, action, AddModuleCertificates);
    }

    @Action(UnsetModuleCertificates)
    unsetAll(ctx: StateContext<ModuleCertificatesStateModel>): void {
        this._unsetSubentities(ctx);
    }

    @Action(FetchModuleCertificates)
    fetchAll(ctx: StateContext<ModuleCertificatesStateModel>, { payload }: FetchModuleCertificates): void {
        this._fetchSubentities(
            ctx,
            { payload: payload.moduleId },
            SetModuleCertificates,
            FetchModuleCertificatesSuccess,
            FetchModuleCertificatesFailure,
            this._moduleCertificatesService.getAll(payload.moduleId).pipe(
                map((data) => {
                    return data.map((query) => new ModuleCertificateExtended(query));
                })
            )
        );
    }

    @Action(FetchModuleCertificatesSuccess)
    fetchAllSuccess(ctx: StateContext<ModuleCertificatesStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchModuleCertificatesFailure)
    fetchAllFailure(ctx: StateContext<ModuleCertificatesStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(AddCertificatesToModuleSuccess)
    addCertificatesToModulesAsTemplateSuccess(ctx: StateContext<ModuleCertificatesStateModel>, action: AddCertificatesToModuleSuccess): void {
        const moduleCertificates = this.store.selectSnapshot(ModuleCertificateState.getEntities);
        const currentModule = this.store.selectSnapshot(ModuleState.getEntity);

        if (currentModule) {
            this._setSubentities(ctx, { payload: moduleCertificates.filter((moduleCertificate) => moduleCertificate.moduleId === currentModule.id) }, AddModuleCertificates);
        }
    }
}
